import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";

const PACKAGES = [
  {
    key: "reservations",
    name: "Reservation Service",
    priceByDest: { mallorca: "€250", morocco: "€375", ibiza: "€375", courchevel: "€450" },
    defaultPrice: "€375",
    priceSuffix: "/week",
    pricePrefix: "from",
    description: "Restaurants, beach clubs and nightlife — all secured before you arrive.",
  },
  {
    key: "signature",
    name: "Signature Planning",
    priceByDest: null,
    defaultPrice: "€900",
    priceSuffix: "/stay",
    pricePrefix: "",
    description: "A planning call, bespoke itinerary, and full pre-arrival support.",
  },
  {
    key: "ondemand",
    name: "On Demand",
    priceByDest: null,
    defaultPrice: "€1,500",
    priceSuffix: "/week",
    pricePrefix: "",
    description: "Full concierge support throughout your stay, with WhatsApp access until 9pm.",
    badge: "Most Popular",
  },
  {
    key: "vip",
    name: "VIP Concierge",
    priceByDest: null,
    defaultPrice: "€5,000",
    priceSuffix: "/week",
    pricePrefix: "from",
    description: "Total dedication — airport welcome, champagne arrival, WhatsApp until midnight.",
  },
];

const FEATURES = [
  { label: "Restaurant reservations", included: [true, true, true, true] },
  { label: "Beach club bookings", included: [true, true, true, true] },
  { label: "Nightlife access", included: [true, true, true, true] },
  { label: "Pre-arrival planning call", included: [false, true, true, true] },
  { label: "Bespoke itinerary document", included: [false, true, true, true] },
  { label: "Pre-arrival WhatsApp support", included: [false, true, true, true] },
  { label: "WhatsApp support in destination", included: [false, false, "Until 9pm", "Until midnight"] },
  { label: "Booking changes during stay", included: [false, false, true, true] },
  { label: "New bookings during stay", included: [false, false, true, true] },
  { label: "In-person arrival welcome", included: [false, false, true, true] },
  { label: "Airport meet and greet", included: [false, false, false, true] },
  { label: "Champagne villa welcome", included: [false, false, false, true] },
  { label: "Dedicated concierge manager", included: [false, false, false, true] },
  { label: "Priority handling of all requests", included: [false, false, false, true] },
];

export default function ConciergePackagesSection({ services, selectedServiceIds, onSelectService, onConfirm, destination, agentSlug }) {
  const [expandedMobileCard, setExpandedMobileCard] = useState(null);

  const getPrice = (pkg) => {
    if (pkg.priceByDest && destination) {
      const dest = destination.toLowerCase();
      return pkg.priceByDest[dest] || pkg.defaultPrice;
    }
    return pkg.defaultPrice;
  };

  // Filter packages per agent (e.g. hide VIP for charles-marlow)
  const visiblePackages = PACKAGES.filter(pkg => {
    if (pkg.key === "vip" && agentSlug === "charles-marlow") return false;
    return true;
  });

  // Filter features to only show rows relevant to visible packages
  const visibleFeatures = FEATURES.filter(feature => {
    // Check if at least one visible package includes this feature
    return visiblePackages.some(pkg => {
      const origIndex = PACKAGES.indexOf(pkg);
      return feature.included[origIndex] === true || typeof feature.included[origIndex] === "string";
    });
  });

  const getServiceForPackage = (pkg) => {
    if (!services) return null;
    const key = pkg.key;
    if (key === "reservations") return services.find(s => s.name?.toLowerCase().includes("reservation"));
    if (key === "signature") return services.find(s => s.name?.toLowerCase().includes("signature") || s.name?.toLowerCase().includes("planning"));
    if (key === "ondemand") return services.find(s => s.name?.toLowerCase().includes("on-demand") || s.name?.toLowerCase().includes("on demand"));
    if (key === "vip") return services.find(s => s.name?.toLowerCase().includes("vip"));
    return null;
  };

  const isSelected = (pkg) => {
    const svc = getServiceForPackage(pkg);
    return svc && selectedServiceIds?.includes(svc.id);
  };

  const handleSelect = (pkg) => {
    const svc = getServiceForPackage(pkg);
    if (svc && onSelectService) onSelectService(svc);
  };

  const selectedPkg = visiblePackages.find(p => isSelected(p));

  const getFeaturesForPackage = (pkgIndex) => {
    return visibleFeatures.filter(f => f.included[pkgIndex]).map(f => ({
      label: f.label,
      value: typeof f.included[pkgIndex] === "string" ? f.included[pkgIndex] : true,
    }));
  };

  const renderCell = (value, isHighlightCol) => {
    if (typeof value === "string") {
      return <span className="text-[#D4AF37] text-xs font-medium">{value}</span>;
    }
    if (value === true) {
      return <Check className="w-[18px] h-[18px] text-[#D4AF37] mx-auto" />;
    }
    return <Minus className="w-4 h-4 text-[#c5b99a]/40 mx-auto" />;
  };

  return (
    <section
      data-testid="concierge-packages-section"
      className="relative"
      style={{
        background: "#faf8f5",
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      {/* Divider */}
      <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/25 to-transparent" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-10 pb-16 md:pt-12 md:pb-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2
            className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#2c2a26] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
          >
            Choose Your Concierge Experience
          </h2>
          <p className="text-base md:text-lg text-[#6b6560] max-w-2xl mx-auto font-light leading-relaxed">
            Pre-arrival services are included as part of your Villa booking. If you would like additional support during your stay, you can choose to upgrade to one of the following concierge options.
          </p>
          {agentSlug === "charles-marlow" && (
            <p className="text-sm text-[#8a847d] max-w-2xl mx-auto mt-4 leading-relaxed">
              From restaurant reservations to a fully managed luxury stay — choose the level of support that suits you.
            </p>
          )}
        </motion.div>

        {/* Desktop: Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          
          {/* Left Column — Package Cards */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            {visiblePackages.map((pkg, index) => {
              const selected = isSelected(pkg);
              const isPopular = pkg.badge === "Most Popular";
              const pkgFeatures = getFeaturesForPackage(index);
              const mobileExpanded = expandedMobileCard === index;

              return (
                <motion.div
                  key={pkg.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                >
                  <button
                    data-testid={`concierge-card-${pkg.key}`}
                    onClick={() => handleSelect(pkg)}
                    className={`relative w-full text-left p-5 rounded-xl transition-all duration-300 group ${
                      selected
                        ? "border-2 border-[#D4AF37] bg-white shadow-[0_4px_24px_rgba(212,175,55,0.12)]"
                        : isPopular
                          ? "border border-[#D4AF37]/40 bg-white hover:border-[#D4AF37]/70 shadow-sm"
                          : "border border-[#e8e4de] bg-white hover:border-[#d0c9be] shadow-sm"
                    }`}
                  >
                    {/* Most Popular Badge */}
                    {isPopular && (
                      <span className="absolute -top-3 right-5 bg-[#D4AF37] text-white text-[10px] font-bold uppercase tracking-widest py-1 px-3 rounded-full">
                        Most Popular
                      </span>
                    )}

                    {/* Selected Check */}
                    {selected && (
                      <span className="absolute top-4 right-4 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-white" />
                      </span>
                    )}

                    <div className="flex flex-col">
                      <h3
                        className="text-lg text-[#2c2a26] font-medium mb-1 pr-8"
                        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.35rem", fontWeight: 600 }}
                      >
                        {pkg.name}
                      </h3>
                      <div className="flex items-baseline gap-1.5 mb-2">
                        {pkg.pricePrefix && <span className="text-[#8a847d] text-xs">{pkg.pricePrefix}</span>}
                        <span className="text-[#D4AF37] text-lg font-semibold">{getPrice(pkg)}</span>
                        <span className="text-[#8a847d] text-sm">{pkg.priceSuffix}</span>
                      </div>
                      <p className="text-[#6b6560] text-sm leading-relaxed">{pkg.description}</p>
                    </div>

                    {/* Select Button */}
                    <div className={`mt-4 w-full py-2.5 rounded-lg text-center text-sm font-medium transition-all duration-200 ${
                      selected
                        ? "bg-[#D4AF37] text-white"
                        : isPopular
                          ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/20"
                          : "bg-[#f5f2ed] text-[#6b6560] border border-[#e8e4de] group-hover:bg-[#ece8e1] group-hover:text-[#2c2a26]"
                    }`}>
                      {selected ? "Selected" : "Select"}
                    </div>
                  </button>

                  {/* Mobile: Expanded feature list below each card */}
                  <div className="lg:hidden">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMobileCard(mobileExpanded ? null : index);
                      }}
                      className="w-full text-center py-2 text-xs text-[#8a847d] hover:text-[#6b6560] transition-colors"
                      data-testid={`toggle-features-${pkg.key}`}
                    >
                      {mobileExpanded ? "Hide features" : `View features (${pkgFeatures.length})`}
                    </button>
                    {mobileExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="px-4 pb-3"
                      >
                        <div className="space-y-2.5 pt-1">
                          {pkgFeatures.map((feat, fi) => (
                            <div key={fi} className="flex items-center gap-2.5">
                              <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                              <span className="text-sm text-[#6b6560]">
                                {feat.label}
                                {typeof feat.value === "string" && (
                                  <span className="text-[#D4AF37] ml-1">({feat.value})</span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Right Column — Feature Matrix (desktop only) */}
          <div className="hidden lg:block lg:col-span-8 rounded-xl border border-[#e8e4de] bg-white overflow-hidden">
            <table className="w-full text-left" data-testid="feature-comparison-matrix">
              {/* Table Header */}
              <thead>
                <tr className="border-b border-[#e8e4de]">
                  <th className="py-4 px-6 text-xs uppercase tracking-widest text-[#8a847d] font-medium w-[36%]">Feature</th>
                  {visiblePackages.map((pkg, i) => {
                    const origIndex = PACKAGES.indexOf(pkg);
                    return (
                    <th
                      key={pkg.key}
                      className={`py-4 px-2 text-center text-xs uppercase tracking-wider font-medium ${
                        origIndex === 2 ? "text-[#D4AF37] bg-[#D4AF37]/[0.04]" : "text-[#8a847d]"
                      }`}
                      style={{ width: "16%" }}
                    >
                      <span className="block" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "0.85rem", fontWeight: 600, textTransform: "none", letterSpacing: "0" }}>
                        {pkg.name}
                      </span>
                    </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleFeatures.map((feature, fi) => (
                  <tr
                    key={fi}
                    className="border-b border-[#f0ece6] last:border-0 hover:bg-[#faf8f5] transition-colors"
                  >
                    <td className="py-3.5 px-6 text-sm text-[#6b6560]">{feature.label}</td>
                    {visiblePackages.map((pkg) => {
                      const origIndex = PACKAGES.indexOf(pkg);
                      const value = feature.included[origIndex];
                      return (
                      <td
                        key={pkg.key}
                        className={`py-3.5 px-2 text-center ${origIndex === 2 ? "bg-[#D4AF37]/[0.04]" : ""}`}
                      >
                        {renderCell(value, origIndex === 2)}
                      </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar */}
      {selectedPkg && (
        <div
          className="lg:hidden fixed bottom-0 left-0 w-full z-50 border-t border-[#e8e4de] p-4 flex items-center justify-between"
          style={{ background: "rgba(250, 248, 245, 0.97)", backdropFilter: "blur(20px)" }}
          data-testid="mobile-upgrade-sticky-bar"
        >
          <div>
            <p className="text-[#2c2a26] text-sm font-medium">{selectedPkg.name}</p>
            <p className="text-[#D4AF37] text-xs">
              {selectedPkg.pricePrefix && `${selectedPkg.pricePrefix} `}{getPrice(selectedPkg)}{selectedPkg.priceSuffix}
            </p>
          </div>
          <button
            data-testid="mobile-confirm-upgrade-btn"
            onClick={onConfirm}
            className="bg-[#D4AF37] text-white font-semibold px-6 py-2.5 rounded-lg hover:bg-[#C5A059] transition-colors text-sm"
          >
            Confirm
          </button>
        </div>
      )}
    </section>
  );
}
